import { ArrowLeft, Eye, RefreshCw, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { DateInput } from "@/components/ui/DateInput";
import { Loader } from "@/components/ui/Loader";
import { cn } from "@/utils/cn";
import {
  extractApiError,
  fetchContratoArquivado,
  fetchContratosArquivados,
  type ContratoArquivadoDto,
  type ContratoArquivadoFilters,
  type MaquinarioDto,
} from "@/services/locacoes";
import { formatDate, formatDateTime, formatNumber } from "@/utils/format";
import { currentLocalDateInput } from "@/utils/dateInput";
import { formatContratoValor } from "./locacoesUtils";

interface Props {
  open: boolean;
  onClose: () => void;
  maquinarios: MaquinarioDto[];
}

function emptyFilters(): ContratoArquivadoFilters {
  const currentDate = currentLocalDateInput();
  return {
    q: "",
    clienteSiengeId: "",
    maquinaId: "",
    deletedFrom: currentDate,
    deletedTo: currentDate,
  };
}

function maquinasLabel(ids: string[], maquinarios: MaquinarioDto[]): string {
  if (ids.length === 0) return "—";
  const names = ids
    .map((id) => maquinarios.find((m) => m.id === id)?.nome ?? id)
    .filter(Boolean);
  if (names.length <= 2) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
}

function cleanFilters(filters: ContratoArquivadoFilters): ContratoArquivadoFilters {
  const cleaned: ContratoArquivadoFilters = {};
  if (filters.q?.trim()) cleaned.q = filters.q.trim();
  if (filters.clienteSiengeId?.trim()) cleaned.clienteSiengeId = filters.clienteSiengeId.trim();
  if (filters.maquinaId?.trim()) cleaned.maquinaId = filters.maquinaId.trim();
  if (filters.deletedFrom?.trim()) cleaned.deletedFrom = filters.deletedFrom.trim();
  if (filters.deletedTo?.trim()) cleaned.deletedTo = filters.deletedTo.trim();
  return cleaned;
}

function ContratoArquivadoCard({
  item,
  maquinarios,
  onView,
}: {
  item: ContratoArquivadoDto;
  maquinarios: MaquinarioDto[];
  onView: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-5 gap-y-1 text-sm">
        <div className="min-w-[140px]">
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400">Cliente</span>
          <span className="block truncate font-medium text-slate-800">{item.contrato.clienteSiengeNome}</span>
          {item.contrato.clienteSiengeDocument && (
            <span className="block truncate text-xs text-slate-500">{item.contrato.clienteSiengeDocument}</span>
          )}
        </div>
        <div className="min-w-[120px] flex-1">
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400">Modelo</span>
          <span className="block truncate text-slate-800">{item.contrato.modeloContrato}</span>
        </div>
        <div className="min-w-[120px]">
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400">Máquinas</span>
          <span className="block truncate text-slate-700">
            {maquinasLabel(item.contrato.maquinaIds, maquinarios)}
          </span>
        </div>
        <div className="min-w-[100px]">
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400">Excluído em</span>
          <span className="whitespace-nowrap text-slate-700">{formatDateTime(item.deletedAt)}</span>
          {item.deletedBy?.userName && (
            <span className="block text-xs text-slate-500">por {item.deletedBy.userName}</span>
          )}
        </div>
        <div className="min-w-[72px]">
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400">Horímetro</span>
          <span className="font-medium text-slate-800">{item.registrosHorimetro.length}</span>
        </div>
        <div className="min-w-[72px]">
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400">Abastec.</span>
          <span className="font-medium text-slate-800">{item.abastecimentos.length}</span>
        </div>
      </div>
      <div className="ml-auto flex flex-shrink-0 items-center gap-1.5 sm:gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
          Excluído
        </span>
        <button
          type="button"
          onClick={() => onView(item.id)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-brand-50 hover:text-brand-700"
          title="Ver detalhes"
          aria-label="Ver detalhes do contrato excluído"
        >
          <Eye className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function ContratoArquivadoDetail({
  detail,
  maquinarios,
  onBack,
}: {
  detail: ContratoArquivadoDto;
  maquinarios: MaquinarioDto[];
  onBack: () => void;
}) {
  return (
    <div className="space-y-4">
      <Button type="button" variant="ghost" size="sm" leftIcon={<ArrowLeft className="h-4 w-4" />} onClick={onBack}>
        Voltar à lista
      </Button>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-lg font-semibold text-slate-800">{detail.contrato.clienteSiengeNome}</p>
            <p className="text-sm text-slate-500">{detail.contrato.clienteSiengeDocument || "—"}</p>
            <p className="mt-1 text-sm text-slate-600">{detail.contrato.modeloContrato}</p>
          </div>
          <div className="text-right text-sm text-slate-600">
            <p>
              Excluído em <span className="font-medium">{formatDateTime(detail.deletedAt)}</span>
            </p>
            {detail.deletedBy?.userName && (
              <p>
                por <span className="font-medium">{detail.deletedBy.userName}</span>
              </p>
            )}
          </div>
        </div>
        <dl className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="text-xs font-medium uppercase text-slate-500">Período</dt>
            <dd className="text-sm text-slate-800">
              {formatDate(detail.contrato.dataInicio)}
              {detail.contrato.dataFimPrevista ? ` — ${formatDate(detail.contrato.dataFimPrevista)}` : ""}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase text-slate-500">Valor</dt>
            <dd className="text-sm text-slate-800">{formatContratoValor(detail.contrato)}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase text-slate-500">Máquinas</dt>
            <dd className="text-sm text-slate-800">{maquinasLabel(detail.contrato.maquinaIds, maquinarios)}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase text-slate-500">ID</dt>
            <dd className="font-mono text-xs text-slate-600">{detail.id}</dd>
          </div>
        </dl>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-slate-800">
            Horímetros ({detail.registrosHorimetro.length})
          </h3>
          {detail.registrosHorimetro.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhum registro arquivado.</p>
          ) : (
            <ul className="max-h-64 space-y-2 overflow-y-auto text-sm">
              {detail.registrosHorimetro.map((r) => (
                <li key={r.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <span className="font-medium">{r.maquinaNome}</span> — {formatDateTime(r.dataHora)} —{" "}
                  {formatNumber(r.horimetroInformado ?? r.horimetroAtual)} h
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-slate-800">
            Abastecimentos ({detail.abastecimentos.length})
          </h3>
          {detail.abastecimentos.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhum abastecimento arquivado.</p>
          ) : (
            <ul className="max-h-64 space-y-2 overflow-y-auto text-sm">
              {detail.abastecimentos.map((a) => (
                <li key={a.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <span className="font-medium">{a.maquinaNome}</span> — {formatDate(a.data)} —{" "}
                  {formatNumber(a.litros)} L — R$ {formatNumber(a.valorTotal)}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-slate-800">Manutenções ({detail.manutencoes.length})</h3>
          {detail.manutencoes.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhuma manutenção arquivada.</p>
          ) : (
            <ul className="max-h-48 space-y-2 overflow-y-auto text-sm">
              {detail.manutencoes.map((m) => (
                <li key={m.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  {m.maquinaNome} — {formatDate(m.dataAbertura)} — {m.status}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-slate-800">Locações ({detail.locacoes.length})</h3>
          {detail.locacoes.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhuma locação arquivada.</p>
          ) : (
            <ul className="max-h-48 space-y-2 overflow-y-auto text-sm">
              {detail.locacoes.map((l) => (
                <li key={l.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  {l.maquinaNome} — {l.status} — {formatDate(l.dataInicio)}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:col-span-2">
          <h3 className="mb-3 text-sm font-semibold text-slate-800">
            Carradas ({detail.carradas?.length ?? 0})
          </h3>
          {!detail.carradas?.length ? (
            <p className="text-sm text-slate-500">Nenhum lançamento de carrada arquivado.</p>
          ) : (
            <ul className="max-h-64 space-y-2 overflow-y-auto text-sm">
              {detail.carradas.map((c) => (
                <li key={c.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  {formatDate(c.data)} — {formatNumber(c.quantidade)} carradas
                  {c.valorUnitario != null ? ` × R$ ${formatNumber(c.valorUnitario)}` : ""}
                  {c.motorista ? ` — ${c.motorista}` : ""}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

export function ModalContratosArquivados({ open, onClose, maquinarios }: Props) {
  const [filters, setFilters] = useState(emptyFilters);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [list, setList] = useState<ContratoArquivadoDto[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ContratoArquivadoDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadList = useCallback(async (nextFilters: ContratoArquivadoFilters) => {
    setLoading(true);
    setError(null);
    try {
      const arquivados = await fetchContratosArquivados(cleanFilters(nextFilters));
      setList(arquivados);
    } catch (e) {
      setError(extractApiError(e, "Erro ao carregar contratos arquivados."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setFilters(emptyFilters());
      setList([]);
      setError(null);
      setSelectedId(null);
      setDetail(null);
      setDetailLoading(false);
      return;
    }
    void loadList(emptyFilters());
  }, [open, loadList]);

  const clientesOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of list) {
      if (item.contrato.clienteSiengeId) {
        map.set(item.contrato.clienteSiengeId, item.contrato.clienteSiengeNome || item.contrato.clienteSiengeId);
      }
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1], "pt-BR"));
  }, [list]);

  const hasActiveFilters = Boolean(
    filters.q?.trim() ||
      filters.clienteSiengeId?.trim() ||
      filters.maquinaId?.trim() ||
      filters.deletedFrom?.trim() ||
      filters.deletedTo?.trim(),
  );

  const openDetail = async (id: string) => {
    setSelectedId(id);
    setDetailLoading(true);
    setError(null);
    try {
      const arquivado = await fetchContratoArquivado(id);
      setDetail(arquivado);
    } catch (e) {
      setError(extractApiError(e, "Erro ao carregar detalhes do contrato arquivado."));
      setSelectedId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
  };

  const handleRefresh = () => void loadList(filters);

  const modalTitle = selectedId ? "Contrato excluído — arquivo" : "Contratos excluídos";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={modalTitle}
      size="full"
      footer={
        <div className="flex justify-end">
          <Button type="button" variant="secondary" onClick={onClose}>
            Fechar
          </Button>
        </div>
      }
    >
      <div className="flex min-h-0 flex-col gap-4">
        {!selectedId && (
          <>
            <p className="text-sm text-slate-500">
              Histórico de contratos removidos com horímetros, abastecimentos e demais dados relacionados.
            </p>

            <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
              <div className="min-w-0 flex-1">
                <Input
                  label="Pesquisar"
                  placeholder="Cliente, documento, modelo ou ID…"
                  value={filters.q ?? ""}
                  onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                  leftIcon={<Search className="h-4 w-4 text-slate-400" />}
                />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:min-w-[320px] lg:w-auto">
                <DateInput
                  label="Excluído de"
                  value={filters.deletedFrom ?? ""}
                  onChange={(deletedFrom) => setFilters((f) => ({ ...f, deletedFrom }))}
                />
                <DateInput
                  label="Excluído até"
                  value={filters.deletedTo ?? ""}
                  onChange={(deletedTo) => setFilters((f) => ({ ...f, deletedTo }))}
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                loading={loading}
                leftIcon={<RefreshCw className="h-4 w-4" />}
                className="self-end lg:mb-0.5"
              >
                Atualizar
              </Button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                label="Cliente"
                value={filters.clienteSiengeId ?? ""}
                onChange={(e) => setFilters((f) => ({ ...f, clienteSiengeId: e.target.value }))}
                placeholder="Todos"
                options={clientesOptions.map(([id, nome]) => ({ value: id, label: nome }))}
              />
              <Select
                label="Máquina"
                value={filters.maquinaId ?? ""}
                onChange={(e) => setFilters((f) => ({ ...f, maquinaId: e.target.value }))}
                placeholder="Todas"
                options={maquinarios.map((m) => ({
                  value: m.id,
                  label: `${m.nome} (${m.codigoInterno})`,
                }))}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" size="sm" onClick={() => void loadList(filters)}>
                Filtrar
              </Button>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={() => {
                    const empty = emptyFilters();
                    setFilters(empty);
                    void loadList(empty);
                  }}
                  className="text-xs font-medium text-brand-600 hover:text-brand-700"
                >
                  Limpar filtros
                </button>
              )}
            </div>
          </>
        )}

        {error && (
          <p className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
        )}

        {selectedId ? (
          detailLoading || !detail ? (
            <div className="flex justify-center py-16">
              <Loader size="lg" label="Carregando contrato..." />
            </div>
          ) : (
            <ContratoArquivadoDetail detail={detail} maquinarios={maquinarios} onBack={closeDetail} />
          )
        ) : (
          <div
            className={cn(
              "min-h-[200px] rounded-xl border border-slate-200 bg-slate-50/50",
              list.length > 0 && "max-h-[min(60vh,520px)] overflow-y-auto",
            )}
          >
            <div className="space-y-2 p-3">
              {loading && list.length === 0 ? (
                <div className="flex justify-center py-10">
                  <Loader size="lg" label="Carregando..." />
                </div>
              ) : list.length === 0 ? (
                <p className="py-10 text-center text-sm text-slate-500">
                  {hasActiveFilters
                    ? "Nenhum contrato excluído encontrado com os filtros aplicados."
                    : "Nenhum contrato excluído encontrado."}
                </p>
              ) : (
                list.map((item) => (
                  <ContratoArquivadoCard
                    key={item.id}
                    item={item}
                    maquinarios={maquinarios}
                    onView={(id) => void openDetail(id)}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
